import React from 'react';
import { BillingPlan, BillingState, BillingTrialStatus } from '../services/apiService';
import { trackClick } from '../utils/analytics';
import { fillCounts, getCorpusStatsSync, loadCorpusStats, onCorpusStats }
  from '../utils/corpusStats';
import { apiService } from '../services/apiService';
import { money } from '../utils/money';

/**
 * Plan comparison / plan detail.
 *
 * With more than one plan to show it renders the comparison table (feature
 * rows × plan columns, current plan highlighted). With exactly one it drops
 * the comparison framing and reads as that plan's detail sheet — a table
 * with a single column compares nothing and just looks broken.
 *
 * `lockedFeature` sets the "Unlock <feature>" framing used when the modal is
 * opened from a gated action rather than from the Billing tab.
 */

interface PricingModalProps {
  plans: BillingPlan[];
  featureOrder: string[];
  currentPlanCode?: string | null;
  lockedFeature?: string;
  busyPlan?: string;
  /** `coupon` and `method` are set once a code has been applied here. A
   *  Razorpay subscription offer covers one payment method, so the method the
   *  customer picked has to travel with the choice. */
  /** payOnce: the applied code moved this checkout to a one-year payment (a code in USD /
   *  EUR / CHF cannot ride on auto-renew), so the caller opens an order, not a subscription. */
  onChoose: (plan: BillingPlan, coupon?: string, method?: string, expectedAmount?: number,
             payOnce?: boolean) => void;
  onClose: () => void;
  /** The trial CTA sits beside the upgrade button whenever trials exist at
   *  all. Every gate in the product renders this modal, so putting it here is
   *  what makes the trial reachable from all of them — and, once used, what
   *  makes the greyed-out button and its reason visible from all of them
   *  too (one trial per account, ever). */
  trial?: Pick<BillingTrialStatus, 'available' | 'days'>
    & Partial<Pick<BillingTrialStatus, 'enabled' | 'mode' | 'reason' | 'used' | 'used_reason'>> | null;
  onStartTrial?: (plan: BillingPlan) => void;
  /** Where the account is in the trial lifecycle. Drives the trial button's
   *  third face — "Trial active · n days left" — which `trial.available`
   *  alone cannot tell from "already used". */
  state?: BillingState | null;
  /** The running trial's countdown, for that label. */
  trialDaysLeft?: number | null;
  /** A headline and message of its own — the upgrade prompt shown after
   *  sign-in (components/upgrade). Replaces the title and the line under it. */
  intro?: { headline: string; message?: string };
}

// A plan's charge for THIS user; the server puts it on `amount`/`currency`.
const priceOf = (p: BillingPlan) => p.amount ?? p.price_inr_paise;
const curOf = (p: BillingPlan) => p.currency || 'INR';

const METHOD_LABEL: Record<string, string> = { card: 'Card', upi: 'UPI' };

const PricingModal: React.FC<PricingModalProps> = ({
  plans, featureOrder, currentPlanCode, lockedFeature, busyPlan, onChoose, onClose,
  trial, onStartTrial, state, trialDaysLeft, intro,
}) => {
  // Plan copy is admin-authored and quotes the catalogue size. Rather than let
  // "27,000+ tech offers" rot every time a university lands, the text is run
  // through fillCounts, which resolves {tech_count} AND rewrites the older
  // hardcoded figures to the live number.
  // Applying a code here rather than sending people to Billing: this modal
  // is where the decision is being made, and bouncing them elsewhere to type
  // a code is where an intent to buy goes to die.
  const [coupon, setCoupon] = React.useState('');
  const [quote, setQuote] = React.useState<any>(null);
  const [couponBusy, setCouponBusy] = React.useState(false);
  const [couponError, setCouponError] = React.useState('');
  // Which method button was pressed. `busyPlan` is a PLAN code, so it cannot
  // tell Card from UPI — both buttons belong to the same plan and both showed
  // "Opening…" the moment either was clicked.
  const [pendingMethod, setPendingMethod] = React.useState('');
  const [corpus, setCorpus] = React.useState(getCorpusStatsSync());
  React.useEffect(() => {
    loadCorpusStats().then(setCorpus);
    return onCorpusStats(setCorpus);
  }, []);
  // Escape closes, and focus lands inside on open and goes back where it was
  // on close. A comment used to claim Escape worked; nothing implemented it.
  //
  // Two effects, not one. Callers pass onClose as an inline arrow, so an
  // effect keyed on it re-runs every render — and the first version of this
  // did exactly that, yanking focus to the × while someone was typing a
  // coupon code, so their Enter closed the modal instead of applying it.
  // Focus is handled once, on mount; the key listener reads the latest
  // onClose through a ref and never needs to re-subscribe.
  const closeRef = React.useRef<HTMLButtonElement>(null);
  // Every way out is recorded as paywall_close {locked_feature, via}, so the
  // funnel can tell "looked and left" from "never saw it". Read through a ref
  // for the same reason as onClose: the Escape listener subscribes once.
  const dismissRef = React.useRef((_via: 'x' | 'backdrop' | 'escape') => {});
  dismissRef.current = (via: 'x' | 'backdrop' | 'escape') => {
    trackClick('paywall_close', {
      locked_feature: lockedFeature || null,
      via,
      intro: intro ? true : undefined,
    });
    onClose();
  };
  React.useEffect(() => {
    const previously = document.activeElement as HTMLElement | null;
    closeRef.current?.focus();
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') dismissRef.current('escape'); };
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('keydown', onKey);
      previously?.focus?.();
    };
  }, []);

  const applyCoupon = async (plan: BillingPlan | null | undefined) => {
    const code = coupon.trim();
    setCouponError(''); setQuote(null);
    if (!code || !plan) return;
    const userId = apiService.getStoredUserId();
    if (!userId) { setCouponError('Sign in to use a code.'); return; }
    setCouponBusy(true);
    const res = await apiService.billingCouponPreview(userId, plan.code, code, true);
    setCouponBusy(false);
    const d: any = res.data;
    if (!res.success || !d?.success) {
      setCouponError(res.error || d?.error || 'That code could not be applied.');
      return;
    }
    if (!d.discount) {
      // The server says WHY — expired, already used, not for this account.
      setCouponError(d.coupon_error || 'That code does not change this price.');
      return;
    }
    if (d.coupon_error) setCouponError(d.coupon_error);
    setQuote(d);
  };
  // Never hand back a code the quote refused (or a campaign beat): the
  // server stops a checkout whose code does not resolve instead of charging
  // the listed price behind it, and the customer accepted the price shown.
  const codeToSend = () => (couponError ? undefined : (coupon.trim() || undefined));
  const live = (t?: string) => fillCounts(t || '', corpus);
  // MVP shows exactly two columns: the free tier and the one paid plan we
  // are selling. Higher tiers exist in the data but are not on the card yet,
  // and a three-column table is a different (harder) buying decision.
  const MVP_PLANS = ['explorer', 'voyager'];
  const shown = [...plans]
    // is_active is checked here as well as in the plan list: a retired plan
    // was still being offered in this modal, so "deactivate" appeared to do
    // nothing on the one screen that sells the plan.
    .filter(p => MVP_PLANS.includes(p.code) && p.is_active !== false)
    .sort((a, b) => a.sort_order - b.sort_order);
  const single = shown.length === 1;
  // The plan we push: the first paid plan above the current one, else the
  // priciest paid plan on offer.
  const currentSort = shown.find(p => p.code === currentPlanCode)?.sort_order ?? -1;
  const target = shown.find(p => p.sort_order > currentSort && priceOf(p) > 0)
    || [...shown].reverse().find(p => priceOf(p) > 0);
  // Two CTAs side by side (Sep 2026 spec): the trial and the paid upgrade are
  // separate decisions and both stay in view. The trial button is HIDDEN only
  // when there is no trial to speak of — switched off globally, or the
  // account already pays — and is otherwise always rendered, greyed once it
  // cannot be pressed: one trial per account, ever, and the server's reason
  // ("You have already used your free trial.") is its caption. A code typed
  // below discounts the upgrade only; it never touches the trial, which is
  // already free.
  const upgradeable = !!(target && target.code !== currentPlanCode);
  const paidNow = state === 'voyager' || state === 'voyager_assigned'
    || !!(currentPlanCode && shown.some(p => p.code === currentPlanCode && priceOf(p) > 0));
  const trialShown = upgradeable && !!onStartTrial && !!trial
    && trial.enabled !== false && !paidNow;
  // `state` is the server's word; the running trial has its own face on the
  // button because `available` alone cannot tell "running" from "used up".
  const trialRunning = trialShown && state === 'trial_active';
  const trialOffered = trialShown && !trialRunning && !trial!.used && trial!.available;
  const trialBlockedReason = trialShown && !trialOffered && !trialRunning
    ? (trial!.reason || 'You have already used your free trial.') : '';
  const daysLeft = Math.max(0, Number(trialDaysLeft ?? 0));

  // Union of declared features, in the canonical order, then anything extra
  // an admin added that the order list doesn't know about.
  // featureOrder (billing_service.FEATURE_ORDER) is the canonical order.
  // Anything an admin has added to a plan that the order list doesn't know
  // about follows, so a new feature is never silently dropped.
  const rows = [
    ...featureOrder.filter(f => shown.some(p => p.features?.[f])),
    ...Array.from(new Set(shown.flatMap(p => Object.keys(p.features || {}))))
      .filter(f => !featureOrder.includes(f)),
  ];

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
         onClick={() => dismissRef.current('backdrop')}>
      <div role="dialog" aria-modal="true" aria-labelledby="pricing-title"
           className="relative bg-white rounded-2xl shadow-lg max-w-[46rem] w-full max-h-[90vh] flex flex-col"
           onClick={e => e.stopPropagation()}>
        {/* A visible way out. A paywall with no × reads as a trap, and people
            who feel trapped leave. Positioned against the card, which does not
            scroll — the content below it does — so it stays in the corner on a
            phone instead of scrolling away with the plans. */}
        <button ref={closeRef} type="button" onClick={() => dismissRef.current('x')} aria-label="Close"
          className="absolute top-4 right-4 z-10 w-8 h-8 rounded-full text-grayscale-1100 hover:bg-grayscale-200 text-xl leading-none">
          ×
        </button>
        <div className="p-5 sm:p-6 overflow-y-auto">
        <div className="w-10 h-10 rounded-xl bg-primary-100/60 flex items-center justify-center mb-3">
          <svg className="w-5 h-5 text-primary-700" viewBox="0 0 24 24" fill="none" aria-hidden="true">
            <rect x="4" y="10" width="16" height="10" rx="2" stroke="currentColor" strokeWidth="1.8" />
            <path d="M8 10V7a4 4 0 0 1 8 0v3" stroke="currentColor" strokeWidth="1.8" />
          </svg>
        </div>

        <h2 id="pricing-title" className="text-xl font-bold text-grayscale-1300 font-grotesk">
          {/* One title for every gate (2026-09-16 design). It used to name the
              feature that tripped the paywall — "Unlock full filtering" — but
              the thing being sold is the same whichever door they came
              through, and naming one feature undersold it. A single plan is
              its own detail sheet whatever opened it: "compare plans" over one
              plan compares nothing. */}
          {intro?.headline
            ? intro.headline
            : single ? shown[0].name : lockedFeature ? 'Unlock full access to Cosmos' : 'Plans & pricing'}
        </h2>
        <p className="text-[13px] text-grayscale-1200 mt-1 mb-3">
          {intro?.message
            ? live(intro.message)
            : single
              ? live(shown[0].description)
              : live('Compare plans and choose the access level that fits your team.')}
        </p>

        {single ? (
          <div className="rounded-xl border border-grayscale-300 overflow-hidden">
            <div className="flex items-baseline justify-between px-4 py-3 bg-primary-100/50">
              <span className="font-bold text-grayscale-1300 font-grotesk">{shown[0].name}</span>
              <span className="text-sm text-grayscale-1200">
                {priceOf(shown[0]) > 0
                  ? `${money(priceOf(shown[0]), curOf(shown[0]))} / user / year`
                  : 'Free'}
              </span>
            </div>
            {rows.map(f => (
              <div key={f} className="flex justify-between px-4 py-2.5 border-t border-grayscale-200 text-sm">
                <span className="text-grayscale-1200">{live(f)}</span>
                <span className="text-grayscale-1300">{live(shown[0].features?.[f]) || '—'}</span>
              </div>
            ))}
          </div>
        ) : (
          /* Side-by-side cards rather than a comparison grid. Each plan lists
             only what IT includes, so a long feature sentence reads as one
             ticked line instead of a table row label repeated across columns
             with a dash under every plan that lacks it. */
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {shown.map(p => {
              const isTarget = p.code === target?.code;
              const paid = priceOf(p) > 0;
              const mine = rows.filter(f => p.features?.[f]);
              return (
                <div key={p.code}
                     className={`rounded-xl border p-4 flex flex-col gap-2 ${
                       isTarget ? 'border-2 border-primary-600 bg-primary-100/30'
                                : 'border-grayscale-300'}`}>
                  {/* The trial rides on the plan it unlocks, so it is named
                      on the card and not only on the button below. */}
                  {isTarget && trialOffered && (
                    <span className="self-start rounded-full bg-primary-600 text-grayscale-100 text-[11px] font-medium px-2.5 py-0.5">
                      {trial!.days}-day free trial
                    </span>
                  )}
                  <div>
                    <div className={`font-grotesk font-bold text-base ${
                      isTarget ? 'text-primary-700' : 'text-grayscale-1300'}`}>
                      {p.name}
                      {p.code === currentPlanCode && (
                        <span className="ml-1.5 text-xs font-normal text-grayscale-1100">(current)</span>
                      )}
                    </div>
                    {paid ? (
                      <div className="flex items-baseline gap-1.5 mt-0.5">
                        <span className="text-2xl font-bold text-grayscale-1300 font-grotesk">
                          {money(priceOf(p), curOf(p))}
                        </span>
                        <span className="text-xs text-grayscale-1100">/ user / year</span>
                      </div>
                    ) : (
                      <div className="text-xl font-bold text-grayscale-1300 font-grotesk mt-0.5">Free</div>
                    )}
                  </div>

                  {mine.length ? (
                    <ul className="flex flex-col gap-1.5 mt-0.5">
                      {mine.map(f => (
                        <li key={f} className={`flex gap-2 text-[13px] ${
                          paid ? 'text-grayscale-1300' : 'text-grayscale-1200'}`}>
                          {/* A tick means "you get this". The free tier's
                              lines describe limits, and a tick beside "basic
                              information only" would read as a benefit. */}
                          {paid ? (
                            <svg className="w-3.5 h-3.5 mt-0.5 shrink-0 text-primary-700" viewBox="0 0 20 20"
                                 fill="none" aria-hidden="true">
                              <path d="M4 10.5l4 4 8-9" stroke="currentColor" strokeWidth="2"
                                    strokeLinecap="round" strokeLinejoin="round" />
                            </svg>
                          ) : (
                            <svg className="w-3.5 h-3.5 mt-0.5 shrink-0 text-grayscale-1100" viewBox="0 0 20 20"
                                 fill="none" aria-hidden="true">
                              <circle cx="10" cy="10" r="7.5" stroke="currentColor" strokeWidth="1.6" />
                              <path d="M7 10h6" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
                            </svg>
                          )}
                          {/* live() on the NAME too: plan copy is admin-authored
                              and a feature name can quote the catalogue size,
                              which would otherwise print a raw {tech_count}. */}
                          <span>{live(f)}</span>
                        </li>
                      ))}
                    </ul>
                  ) : (
                    <span className="text-[13px] text-grayscale-1100 mt-1">
                      {paid ? 'Not available' : 'Limited access'}
                    </span>
                  )}
                </div>
              );
            })}
          </div>
        )}

        {/* Optional code, right under the plans. This modal is where the
            decision is being made; sending someone to Billing to type a code
            is where an intent to buy goes to die. */}
        {target && target.code !== currentPlanCode && (
          <div className="flex flex-col gap-1.5 mt-3">
            <div className="flex items-center gap-2 flex-wrap">
              <input
                value={coupon}
                onChange={e => { setCoupon(e.target.value); setQuote(null); setCouponError(''); setPendingMethod(''); }}
                onKeyDown={e => { if (e.key === 'Enter') applyCoupon(target); }}
                placeholder="Have a coupon or voucher code? (optional)"
                className="h-10 px-3 rounded-lg border border-grayscale-300 bg-white text-[13px] flex-1 min-w-0 focus:border-primary-500 outline-none" />
              <button type="button" onClick={() => applyCoupon(target)}
                disabled={couponBusy || !coupon.trim()}
                className="h-10 px-5 rounded-lg border border-grayscale-300 text-grayscale-1300 text-[13px] font-medium hover:border-primary-500 disabled:opacity-50">
                {couponBusy ? 'Checking…' : 'Apply'}
              </button>
            </div>
            {couponError && <span className="text-xs text-red-600">{couponError}</span>}
            {quote && (
              <span className="text-xs text-primary-700">
                {quote.coupon || 'Discount'} applied —{' '}
                <span className="line-through text-grayscale-1100">
                  {money(quote.base_amount, quote.currency)}
                </span>{' '}
                <b>{money(quote.amount, quote.currency)}</b>
                {quote.pay_once
                  ? ' for one year, paid once (no auto-renewal).'
                  : quote.renews_at && quote.renews_at !== quote.amount
                  ? ` for the first year, then ${money(quote.renews_at, quote.currency)}/year.`
                  : ' for one year.'}
              </span>
            )}
          </div>
        )}

        {/* Two CTAs in the footer (Sep 2026 spec): outlined trial on the
            left, filled upgrade on the right, stacked on a phone. Until then
            this was ONE button — the trial when it could be started, else
            the upgrade — with the other reachable through a text link, which
            hid the paid path from exactly the people the trial was warming
            up, and hid the "already used" state altogether. Book a Demo is
            on the public header; on a payment sheet it read as a way out. */}
        {upgradeable && target && (() => {
          // Once a code is applied AND it covers more than one payment
          // method, the pay button splits: a Razorpay subscription offer
          // covers one method, so the method has to be chosen before the
          // subscription is created. The trial button then sits above the
          // split row at full width rather than squeezing in as a third.
          const split = (quote?.methods || []).length > 1;
          const trialButton = trialShown && (
            <button type="button"
              onClick={() => {
                trackClick('paywall_click_start_trial', {
                  plan: target.code, locked_feature: lockedFeature || null,
                  mode: trial!.mode || 'card', days: trial!.days,
                });
                onStartTrial!(target);
              }}
              disabled={!trialOffered || !!busyPlan}
              // The reason travels on the button too, for a hover on desktop
              // and for a screen reader; the caption below repeats it.
              title={trialBlockedReason || undefined}
              className={`${split ? 'w-full' : 'flex-1'} py-3 px-5 rounded-xl font-grotesk text-sm font-semibold border transition-colors ${
                trialOffered
                  ? 'border-primary-600 text-primary-700 hover:bg-primary-100/60 disabled:opacity-50'
                  : 'border-grayscale-300 bg-grayscale-200 text-grayscale-1100 cursor-not-allowed'}`}
            >{trialRunning
                ? `Trial active · ${daysLeft} day${daysLeft === 1 ? '' : 's'} left`
                : `Start free ${trial!.days}-day trial`}</button>
          );
          const upgradeButtons = split ? (
            <div className="flex flex-col sm:flex-row gap-2">
              {(quote.methods as string[]).map(m => (
                <button key={m} type="button"
                  onClick={() => {
                    trackClick('paywall_click_upgrade', {
                      plan: target.code, plan_name: target.name,
                      locked_feature: lockedFeature || null,
                      price_minor: quote.amount, currency: quote.currency,
                      method: m, coupon: quote.coupon || null,
                    });
                    setPendingMethod(m);
                    onChoose(target, codeToSend(), m, quote.amount);
                  }}
                  disabled={!!busyPlan}
                  className="flex-1 py-3 px-5 rounded-xl font-grotesk text-sm font-semibold bg-primary-600 text-grayscale-100 hover:bg-primary-700 transition-colors disabled:bg-primary-100 disabled:text-primary-300"
                >{busyPlan === target.code && pendingMethod === m
                    ? 'Opening…'
                    : `Pay by ${METHOD_LABEL[m] || m}`}</button>
              ))}
            </div>
          ) : (
            <button type="button"
              onClick={() => {
                trackClick('paywall_click_upgrade', {
                  plan: target.code, plan_name: target.name,
                  locked_feature: lockedFeature || null,
                  price_minor: quote?.amount ?? priceOf(target),
                  currency: quote?.currency ?? curOf(target),
                  coupon: quote?.coupon || null,
                });
                onChoose(target, codeToSend(),
                         (quote?.methods || [])[0],
                         (quote && !couponError ? quote.amount : undefined) ?? priceOf(target),
                         !!(quote?.pay_once && !couponError));
              }}
              disabled={!!busyPlan}
              className={`${trialShown ? 'flex-1' : 'w-full'} py-3 px-5 rounded-xl font-grotesk text-sm font-semibold bg-primary-600 text-grayscale-100 hover:bg-primary-700 transition-colors disabled:bg-primary-100 disabled:text-primary-300`}
            >{busyPlan === target.code ? 'Opening…'
                : quote?.pay_once && !couponError
                  ? `Pay ${money(quote.amount, quote.currency)} for one year`
                  : `Upgrade to ${target.name}`}</button>
          );
          return (
            <div className="mt-4 flex flex-col gap-2">
              {split ? (
                <>
                  {trialButton}
                  {upgradeButtons}
                </>
              ) : (
                <div className="flex flex-col sm:flex-row gap-2">
                  {trialButton}
                  {upgradeButtons}
                </div>
              )}
              {/* One caption for the trial's three faces. Offered: that it
                  needs no card. Blocked: the server's reason. Running: the
                  countdown, and that upgrading is how to keep access. */}
              {trialShown && (
                <p className="text-center text-[11px] text-grayscale-1100">
                  {trialRunning
                    ? `Your trial ends in ${daysLeft} day${daysLeft === 1 ? '' : 's'}. Upgrade any time to keep full access.`
                    : trialBlockedReason
                      ? trialBlockedReason
                      : 'No card required for trial.'}
                </p>
              )}
            </div>
          );
        })()}
        </div>
      </div>
    </div>
  );
};

export default PricingModal;
